// src/lib/routeFormat.js
//
// Distance formatting for the route view. THE ONLY PLACE ROUNDING HAPPENS.
//
// ── WHY THIS IS A SEPARATE MODULE ───────────────────────────────────────────
//
// The store keeps raw metres and never rounds, so every rounding decision in
// the feature lives here and can be tested without a component. The rule the
// API's own numbers impose:
//
//   Render from routes[0].distance and step.distance — the metres the API
//   returned — NEVER from a client-side length of the returned geometry.
//
// Those two disagree by up to ~3 m (REM-267). pgr_createTopology joins two
// endpoints within 2 m into one vertex WITHOUT moving either geometry, so
// consecutive edges sit fractionally apart and the assembled line is slightly
// shorter than the summed edge lengths. The API's distance is the correct
// figure. At 0.1 mi (161 m) the gap is invisible; at 10 ft (3.05 m) it is
// exactly one display unit, which is why the rule matters on short steps and
// why measuring the geometry would quietly produce a different number.
//
// Totals: miles to one decimal.
// Steps:  feet to the nearest ten under a quarter mile, otherwise miles to one
//         decimal.

const M_PER_MILE = 1609.344;
const M_PER_FOOT = 0.3048;
const QUARTER_MILE_M = M_PER_MILE / 4; // 402.336

/** Metres → "12.4 mi". Totals only. */
export function formatTotalDistance(metres) {
  if (!Number.isFinite(metres)) return null;
  return `${(metres / M_PER_MILE).toFixed(1)} mi`;
}

/**
 * Metres → "480 ft" or "1.2 mi", per the step rule.
 *
 * Feet round to the nearest TEN, not to the nearest foot: a road traced from
 * imagery is not accurate to a foot, and printing one implies it is.
 */
export function formatStepDistance(metres) {
  if (!Number.isFinite(metres)) return null;
  if (metres < QUARTER_MILE_M) {
    const feet = Math.round(metres / M_PER_FOOT / 10) * 10;
    return `${feet} ft`;
  }
  return `${(metres / M_PER_MILE).toFixed(1)} mi`;
}

// Maneuver → short imperative. `type` and `modifier` come straight from the
// API, which derives them from signed bearing deltas.
//
// NO ROAD NAMES ANYWHERE. The corpus carries none — `name` is null on every
// step by design — so there is nothing to append and no placeholder to invent.
// "Turn left onto Unnamed Road" would be a fabricated fact in the one place a
// driver is most likely to trust it.
export function maneuverLabel(maneuver) {
  if (!maneuver) return "Continue";
  const { type, modifier } = maneuver;
  if (type === "depart") return "Start";
  if (type === "arrive") return "Arrive";
  if (type === "uturn") return "Make a U-turn";
  if (type === "continue") return "Continue straight";
  if (type === "turn") {
    switch (modifier) {
      case "left": return "Turn left";
      case "right": return "Turn right";
      case "slight left": return "Bear left";
      case "slight right": return "Bear right";
      case "sharp left": return "Sharp left";
      case "sharp right": return "Sharp right";
      default: return "Turn";
    }
  }
  return "Continue";
}

/** "3 hours ago" / "just now" — for the stale-cache banner. */
export function formatAge(fetchedAt, now = Date.now()) {
  if (!Number.isFinite(fetchedAt)) return null;
  const mins = Math.floor((now - fetchedAt) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 minute ago";
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return "1 hour ago";
  if (hrs < 24) return `${hrs} hours ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}
